/* eslint-disable @typescript-eslint/ban-types */
import React, { ReactElement, useContext, useEffect, useState } from 'react'
import moment from 'moment'
import i18n from 'live/services/i18n'
import locale from 'live/init/global-locale'
import ptbr from 'date-fns/locale/pt-BR'
import engb from 'date-fns/locale/en-GB'
import { dateFormat, timestampToDateTime, regexNumberAndComma, positiveNumberRegex } from '../utils/parse'
import { StaticDataEvent, Uom, ValueUom } from '../utils/type'
import { MenuItem, OverlayTrigger, Tooltip } from 'react-bootstrap'
import Draggable from 'react-draggable'
import DatePicker, { registerLocale } from 'react-datepicker'
import Modal from '@material-ui/core/Modal'
import onClickOutside from 'react-onclickoutside'
import {
    ModalContent,
    ModalTabSectionsContainer,
    StyledModalField,
    StyledModalContainer,
    CustomDatePickerContainer,
    CustomMeasureSelectContainer,
    CustomDropdownButton,
    ModalWidth,
    ModalPositioningContainer,
    ModalTooltipIcon,
    InformationIcon,
    CustomUnitLabel,
    StyledMissingInformationField,
    BadgeContainer
} from './modal-components-style'
import { StaticDataContext } from '../context/StaticDataContext'
import MaskedInput from 'react-maskedinput'

registerLocale('en-GB', engb)
registerLocale('pt-BR', ptbr)

let calendarLanguage
locale === 'en_us' ? (calendarLanguage = 'en-GB') : (calendarLanguage = 'pt-BR')

export interface CustomModalsProps {
    title?: string
    closeModal: () => void
}

interface ButtonAction {
    text?: string
    action?: (newEvent: StaticDataEvent) => void
    apiAction?: (data: any, action?: string) => void
}
interface StaticDataModalProps {
    isOpen: boolean
    content: ReactElement
    closeModal: () => void
    isDouble?: boolean
}

export const StaticDataModal = ({ isOpen, content, closeModal, isDouble }: StaticDataModalProps): ReactElement => (
    <>
        {isOpen && (
            <Draggable handle="#modal-draggable-handle">
                <Modal open={isOpen} onClose={closeModal} hideBackdrop={true}>
                    <>
                        <ModalContainerComponent
                            isDouble={!!isDouble}
                            content={<ModalContent>{content}</ModalContent>}
                        />
                    </>
                </Modal>
            </Draggable>
        )}
    </>
)

interface ModalTabSectionsProps {
    selected: boolean
    label?: string
    content: ReactElement
    style?: object
}

export const ModalTabSections = ({ selected, content, style }: ModalTabSectionsProps): ReactElement =>
    selected && (
        <div className="modal-tabsections" style={style}>
            <ModalTabSectionsContainer>{content}</ModalTabSectionsContainer>
        </div>
    )

interface ModalFieldProps {
    id?: string
    label?: string
    field: ReactElement
    enabled?: boolean
    errorMessage?: string
    style?: object
    labelAditionalField?: ReactElement
}

export const ModalField = ({
    id,
    label,
    field,
    enabled = true,
    errorMessage,
    style,
    labelAditionalField
}: ModalFieldProps): ReactElement => {
    const { liveTheme } = useContext(StaticDataContext)

    return (
        <StyledModalField style={style} enabled={enabled} liveTheme={liveTheme}>
            {label && (
                <div
                    className="modal-field-label"
                    style={labelAditionalField && { flexDirection: 'row', alignItems: 'center' }}
                >
                    <strong>{i18n(label)}</strong>
                    {i18n(labelAditionalField)}
                </div>
            )}
            {field}
            {id}
            {/* <RequiredFieldMessage message={errorMessage} /> */}
        </StyledModalField>
    )
}

const RequiredFieldMessage = ({ message }: { message: string }): ReactElement => (
    <span className="required-field-message">{message && '* ' + i18n(message)}</span>
)

interface ModalContainerProps {
    content: ReactElement
    isDouble: boolean
}

const ModalContainerComponent = ({ content, isDouble }: ModalContainerProps): ReactElement => {
    const { tooltipPosition, currentChart } = useContext(StaticDataContext)

    const [topPosition, setTopPosition] = useState(
        currentChart.graphic?.parentGroup?.element?.getBoundingClientRect()?.bottom + 2
    )

    useEffect(() => {
        const widget = document.getElementById('static-data-widget')
        widget.style.pointerEvents = 'none'

        return () => {
            widget.style.pointerEvents = 'auto'
        }
    })

    useEffect(() => {
        if (currentChart.graphic?.parentGroup?.element?.getBoundingClientRect()?.bottom) {
            setTopPosition(currentChart.graphic?.parentGroup?.element?.getBoundingClientRect()?.bottom + 2)
        }
    }, [currentChart.graphic?.parentGroup?.element?.getBoundingClientRect()?.bottom])

    let LEFT_POSITION = tooltipPosition.left - 15

    if (tooltipPosition.left / 2 < ModalWidth / 2) {
        LEFT_POSITION = LEFT_POSITION / 2
    } else if (tooltipPosition.left + ModalWidth / 2 > window.innerWidth) {
        LEFT_POSITION = LEFT_POSITION - ModalWidth * 0.85
    } else {
        LEFT_POSITION = LEFT_POSITION - ModalWidth / 2
    }

    return (
        <ModalPositioningContainer id="modal-draggable-handle" topPosition={topPosition} leftPosition={LEFT_POSITION}>
            <StyledModalContainer isDouble={isDouble}>
                <ModalTooltipIcon
                    className="fas fa-caret-up toogler"
                    topPosition={topPosition - 12}
                    leftPosition={tooltipPosition.left - 6}
                ></ModalTooltipIcon>
                {content}
            </StyledModalContainer>
        </ModalPositioningContainer>
    )
}

interface CustomDatePickerProps {
    date: string | null | Date
    setDate: (x) => void
    style?: { borderColor: string }
    isDisabled?: boolean
}

export const CustomDatePicker = ({ date, setDate, style, isDisabled }: CustomDatePickerProps): ReactElement => {
    const { liveTheme } = useContext(StaticDataContext)

    const handleChange = (e: MouseEvent): void => {
        if (!e) return
        setDate(moment(e).format(dateFormat))
    }

    return (
        <CustomDatePickerContainer className="custom-date-picker" isDisabled={isDisabled} liveTheme={liveTheme}>
            <div style={style} className={`custom-input-wrapper ${isDisabled ? 'isDisabled' : ''}`}>
                <DatePicker
                    placeholderText={i18n('Choose date')}
                    selected={date ? new Date(date) : null}
                    value={date ? moment(date).format(dateFormat) : ''}
                    onChange={e => handleChange(e)}
                    locale={calendarLanguage}
                    customInput={<MaskedInput mask='1111/11/11' />}
                    maxDate={new Date()}
                    adjustDateOnChange
                />
                <i className="fas fa-calendar-alt" />
            </div>
        </CustomDatePickerContainer>
    )
}

interface OptionListProps {
    showOptionList: boolean
    unitOptions: string[]
    handleClick: (x: string) => void
}

export const OptionList = onClickOutside(({ showOptionList, unitOptions, handleClick }: OptionListProps) => (
    <ul className={`unit-list ${showOptionList ? null : 'hideList'}`}>
        {unitOptions.map(option => {
            return (
                <li className={`unit-list--option`} key={option} value={option} onMouseDown={() => handleClick(option)}>
                    {option}
                </li>
            )
        })}
    </ul>
))

export const ModalSectionHeader = ({ label }: { label: string }): ReactElement => (
    <div style={{ fontWeight: 'bold', fontSize: 14, color: 'var(--gray-800)' }}>{i18n(label).toUpperCase()}</div>
)

export const ModalSectionSideHeader = ({ label }: { label: string }): ReactElement => (
    <div
        style={{
            fontWeight: 'bold',
            fontSize: 14,
            padding: '16px',
            borderBottom: '1px solid var(--gray-500)',
            color: 'var(--gray-800)'
        }}
    >
        {i18n(label).toUpperCase()}
    </div>
)

export const PipeConnectionROPSectionHeader = ({
    label,
    quantity
}: {
    label: string
    quantity: string | number
}): ReactElement => (
    <div style={{ fontWeight: 'bold', fontSize: 14, display: 'flex' }}>
        {i18n(label).toUpperCase()}{' '}
        <div style={{ color: '#01C292' }}>
            <span>&nbsp;</span> {quantity}
        </div>
    </div>
)

export const MissingInformationSectionArea = ({
    label,
    children,
    onRefresh,
    isLoading,
    LinkToRigPage
}: {
    label: string
    children: React.ReactNode
    onRefresh: () => void
    isLoading: boolean
    LinkToRigPage: string | undefined
}): ReactElement => {
    const [showMissingInformationField, setShowMissingInformationField] = useState<boolean>(false)

    return (
        <StyledMissingInformationField>
            {showMissingInformationField ? (
                <div className="container-missing-information">
                    <header>
                        <div className="content-missing-information">
                            <i className="fas fa-exclamation-triangle" />
                            <div className="missing-information-label">{i18n(label)}</div>
                        </div>
                        <i
                            className="far fa-times"
                            onClick={() => setShowMissingInformationField(!showMissingInformationField)}
                        />
                    </header>
                    <div className="missing-information-field-text">{children}</div>
                    <div className="btn-link-and-refresh">
                        <button className="btn-link">
                            <a href={LinkToRigPage} target="_blank">
                                {i18n('Go to Rig KPI Goals')}
                            </a>
                        </button>
                        <button className="btn-refresh" onClick={onRefresh}>
                            <i className={`fa ${isLoading ? 'fa-spinner fa-spin' : 'fa-refresh'}`} /> {i18n('Refresh')}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="content-missing-information">
                    <i className="fas fa-exclamation-triangle" style={{ color: '#C43535' }} />
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip style={{ zIndex: 9999 }} id={''}>
                                {i18n('Click to more information')}
                            </Tooltip>
                        }
                    >
                        <div
                            className="missing-information-label"
                            style={{ color: '#C43535', cursor: 'pointer' }}
                            onClick={() => setShowMissingInformationField(!showMissingInformationField)}
                        >
                            {i18n(label)}
                        </div>
                    </OverlayTrigger>
                </div>
            )}
        </StyledMissingInformationField>
    )
}

interface InformationBadgeProps {
    id: string
    tooltipMessage: string
    bgColor: string
    iconClassName: string
    title: string
}

export const InformationBadge = ({ id, tooltipMessage, bgColor, iconClassName, title }: InformationBadgeProps) => {
    return (
        <OverlayTrigger
            placement="top"
            overlay={
                <Tooltip id={id} style={{ zIndex: 9999 }}>
                    {i18n(tooltipMessage)}
                </Tooltip>
            }
        >
            <BadgeContainer bgColor={bgColor}>
                <i className={iconClassName} />
                <p>{i18n(title)}</p>
            </BadgeContainer>
        </OverlayTrigger>
    )
}

interface CustomMeasureSelectProps {
    options: Uom[]
    selectedOption?: Uom
    setSelectedUomValue?: (x) => void
    selectedOptionValues?: ValueUom | null
    disabled?: boolean
}

export const CustomMeasureSelect = ({
    options,
    selectedOption,
    setSelectedUomValue,
    selectedOptionValues,
    disabled
}: CustomMeasureSelectProps): ReactElement => {
    if (!(selectedOption || selectedOptionValues?.uom)) return <></>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [selectedUnit, setSelectedUnit] = useState<Uom>(selectedOption || selectedOptionValues?.uom)

    useEffect(() => {
        setSelectedUnit(selectedOption || selectedOptionValues?.uom)
    }, [selectedOption, selectedOptionValues?.uom])

    return (
        <CustomMeasureSelectContainer>
            {disabled && <CustomUnitLabel>{'(' + selectedUnit?.name + ')'}</CustomUnitLabel>}
            {!disabled && (
                <CustomDropdownButton
                    title={`(${selectedUnit?.name || ''})`}
                    key={selectedUnit?.unitOfMeasure_id}
                    id={`dropdown-basic-${selectedUnit?.unitOfMeasure_id}`}
                >
                    {options?.map(option => (
                        <MenuItem
                            key={option.unitOfMeasure_id}
                            eventKey={option}
                            onClick={() => {
                                setSelectedUnit(option)
                                setSelectedUomValue({
                                    ...selectedOptionValues,
                                    uom: option
                                })
                            }}
                        >
                            {option.name}
                        </MenuItem>
                    ))}
                </CustomDropdownButton>
            )}
        </CustomMeasureSelectContainer>
    )
}

export const InformationIconComponent = (message: string) => (
    <OverlayTrigger
        placement="top"
        overlay={
            <Tooltip style={{ zIndex: 9999 }} id={''}>
                {i18n(message)}
            </Tooltip>
        }
    >
        <InformationIcon className="far fa-info-circle" />
    </OverlayTrigger>
)

export const disabledTimeInput = (time: any) => (
    <MaskedInput type="text" mask="11:11" value={timestampToDateTime(time as number).time} disabled />
)

export const disabledDatePicker = (date: any) => (
    <CustomDatePicker date={timestampToDateTime(date as number).date} setDate={undefined} isDisabled />
)

interface NumberInputProps extends React.HTMLProps<HTMLInputElement> {
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
    value: string | number | readonly string[] | undefined
    negativeNumbersAllowed?: boolean
}
interface GenericNumberInputProps extends NumberInputProps {
    regex: RegExp
}

export const NumberInput = (props: NumberInputProps): JSX.Element => {
    return <GenericNumberInput {...props} regex={regexNumberAndComma} />
}
export const IntegerInput = (props: NumberInputProps): JSX.Element => {
    return <GenericNumberInput {...props} regex={positiveNumberRegex} />
}
const GenericNumberInput = ({
    onChange,
    value,
    regex,
    type = 'text',
    negativeNumbersAllowed = false,
    ...props
}: GenericNumberInputProps): JSX.Element => {
    const [val, setVal] = useState(String(value ?? ''))

    useEffect(() => {
        value !== val && setVal(value)
    }, [value])

    return (
        <input
            type="text"
            value={val}
            onChange={e => {
                if (
                    regex.test(e?.target?.value) ||
                    e?.target?.value === '' ||
                    (e?.target?.value === '-' && negativeNumbersAllowed)
                ) {
                    setVal(e?.target?.value)
                } else {
                    e.target.value = val
                }
                onChange && onChange(e)
            }}
            {...props}
        />
    )
}

export const GenericWarning = ({ message }: { message?: string }): ReactElement => {
    return (
        <>
            {message && (
                <div
                    style={{
                        color: '#C43535',
                        display: 'flex',
                        gap: '.5rem',
                        padding: '25px 20px 0px',
                        fontSize: '13px',
                        alignItems: 'center'
                    }}
                >
                    <i className="fas fa-exclamation-triangle"></i>
                    <span>{i18n(message)}</span>
                </div>
            )}
        </>
    )
}
